import { IconArrowLeft, IconInfoCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { type RefObject, useEffect, useRef, useState } from "react";
import { StrategyAssetButton } from "@/components/operating-strategy/StrategyAssetButton";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { strategySnapshotQuery } from "@/lib/strategy-snapshot";
import {
  AUTO_EXPLORATION_STRATEGY,
  BUILTIN_STRATEGIES,
  type Strategy,
} from "@/mocks/operating-strategy";
import { getProductTacticContext, STRATEGY_PRODUCT } from "@/mocks/strategy-product";
import { useAuthStore } from "@/stores/auth";

export const STRATEGY_SOURCE_LABELS = {
  builtin: "内置策略",
  custom: "自定义策略",
  ai: "AI 生成策略",
};

export function StrategyDetailSheet({
  strategy,
  onClose,
  returnFocus,
}: {
  strategy: Strategy | null;
  returnFocus?: RefObject<HTMLElement | null>;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Sheet
      open={strategy !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        finalFocus={returnFocus}
        className="gap-0 data-[side=right]:w-full sm:max-w-[620px]"
        aria-describedby="strategy-detail-description"
      >
        {strategy && <StrategyDetail key={strategy.id} strategy={strategy} onClose={onClose} />}
      </SheetContent>
    </Sheet>
  );
}

function StrategyDetail({
  strategy,
  onClose,
}: {
  strategy: Strategy;
  onClose: () => void;
}): React.ReactElement {
  const userId = useAuthStore((s) => s.user?.id);
  const shops = useAuthStore((s) => s.shops);
  const {
    data: snapshot,
    isPending: snapshotPending,
    isError: snapshotError,
    refetch,
  } = useQuery({
    ...strategySnapshotQuery(
      userId ?? "",
      shops.map((shop) => shop.shopId),
    ),
    enabled: Boolean(strategy.asin === STRATEGY_PRODUCT.asin && userId),
  });
  const [tactic, setTactic] = useState<Strategy | null>(null);
  const backRef = useRef<HTMLButtonElement | null>(null);
  const tacticButtons = useRef(new Map<string, HTMLButtonElement>());
  const lastTacticId = useRef<string | null>(null);
  useEffect(() => {
    if (tactic) backRef.current?.focus();
    else if (lastTacticId.current) tacticButtons.current.get(lastTacticId.current)?.focus();
  }, [tactic]);
  const current = tactic ?? strategy;
  const builtIn = current.source === "builtin";
  const tactics = [...BUILTIN_STRATEGIES, AUTO_EXPLORATION_STRATEGY]
    .filter((item) => current.tacticIds.includes(item.id))
    .map((item) => {
      const context =
        current.asin === STRATEGY_PRODUCT.asin
          ? getProductTacticContext(
              snapshot,
              item.id,
              strategy.source === "ai" ? strategy.goal : undefined,
            )
          : undefined;
      return context ? { ...item, description: context.reason, sections: context.sections } : item;
    });
  return (
    <>
      <SheetHeader className="gap-3 border-b border-line px-7 pb-6 pt-8">
        {tactic && (
          <Button
            ref={backRef}
            className="w-fit"
            variant="ghost"
            size="sm"
            onClick={() => setTactic(null)}
          >
            <IconArrowLeft />
            返回策略详情
          </Button>
        )}
        <Badge className="w-fit" variant="neutral">
          {STRATEGY_SOURCE_LABELS[current.source]}
        </Badge>
        <SheetTitle className="pr-5 text-2xl">{current.name}</SheetTitle>
        <SheetDescription id="strategy-detail-description">
          {builtIn ? "了解打法的优势、限制与适用情形。" : "查看策略设定、采用的打法与商品基线。"}
        </SheetDescription>
      </SheetHeader>
      <div className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
        {builtIn ? (
          <>
            <Badge variant="neutral" className="mb-6">
              {current.goal}
            </Badge>
            <div className="flex flex-col gap-6">
              {!current.sections?.length && (
                <p className="text-sm leading-6 text-muted-foreground">
                  {current.description} 详细打法资料待补充。
                </p>
              )}
              {current.sections?.map((section) => (
                <section key={section.title} className="border-b border-line pb-5 last:border-0">
                  <h3 className="mb-3 text-sm font-semibold">{section.title}</h3>
                  <p className="whitespace-pre-line text-sm leading-6 text-muted-foreground">
                    {section.body}
                  </p>
                </section>
              ))}
            </div>
            <p className="mt-5 text-xs text-muted-foreground">
              {strategy.asin === STRATEGY_PRODUCT.asin
                ? "根据 B0GG6YTX2H 运营报告与关键词筛选整理"
                : current.sections?.length
                  ? "根据内置打法资料整理 · 适用性需结合商品判断"
                  : "暂未提供完整策略资料，不作为投放建议。"}
            </p>
          </>
        ) : (
          <>
            {current.description && (
              <section className="mb-6 rounded-lg border border-line bg-subtle p-5">
                <h3 className="mb-2 text-sm font-semibold">策略摘要</h3>
                <p className="text-sm leading-6 text-muted-foreground">{current.description}</p>
              </section>
            )}
            {current.asin === STRATEGY_PRODUCT.asin && (
              <div className="mb-6 flex flex-wrap gap-2">
                <StrategyAssetButton
                  kind="report"
                  action="preview"
                  label="查看运营报告"
                  fileName={STRATEGY_PRODUCT.reportFileName}
                />
                <StrategyAssetButton
                  kind="keywords"
                  action="download"
                  label="下载关键词筛选"
                  fileName={STRATEGY_PRODUCT.keywordFileName}
                />
              </div>
            )}
            <dl>
              {[
                ["关联商品", current.asin || "暂无"],
                ...(current.productStage ? [["商品阶段", current.productStage]] : []),
                ["业务目标", current.goal],
                ["方案执行", current.mode || "暂无"],
              ].map(([label, value]) => (
                <div
                  className="flex justify-between gap-6 border-b border-line py-3.5 text-sm"
                  key={label}
                >
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            {current.sections?.length ? (
              <div className="mt-7 space-y-4">
                {current.sections.map((section) => (
                  <section key={section.title} className="border-b border-line pb-4 last:border-0">
                    <h3 className="mb-2 text-sm font-semibold">{section.title}</h3>
                    <p className="text-sm leading-6 text-muted-foreground">{section.body}</p>
                  </section>
                ))}
              </div>
            ) : null}
            {current.campaigns?.length ? (
              <section className="mt-7">
                <h3 className="mb-3 text-base font-semibold">拟定投放结构</h3>
                <ul className="space-y-2">
                  {current.campaigns.map((campaign) => (
                    <li key={campaign} className="rounded-lg border border-line p-3 text-sm">
                      {campaign}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">拟定结构尚未提交到广告账户。</p>
              </section>
            ) : null}
            <h3 className="mb-4 mt-7 text-base font-semibold">采用的打法</h3>
            <div className="space-y-3">
              {tactics.map((item) => (
                <section key={item.id} className="rounded-lg border border-line p-5">
                  <h4 className="font-medium">{item.name}</h4>
                  <p className="my-3 text-sm leading-6 text-muted-foreground">{item.description}</p>
                  <Button
                    variant="outline"
                    ref={(element) => {
                      if (element) tacticButtons.current.set(item.id, element);
                      else tacticButtons.current.delete(item.id);
                    }}
                    onClick={() => {
                      lastTacticId.current = item.id;
                      setTactic(item);
                    }}
                  >
                    查看打法详情
                  </Button>
                </section>
              ))}
              {tactics.length === 0 && (
                <p className="text-sm text-muted-foreground">暂无关联打法。</p>
              )}
            </div>
            <h3 className="mb-4 mt-7 text-base font-semibold">商品经营基线</h3>
            {snapshot ? (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-line bg-subtle p-4 text-sm">
                <p>商品销量：{snapshot.metrics.units.toLocaleString("en-US")} 件</p>
                <p>件/会话：{(snapshot.metrics.unitSessionRate * 100).toFixed(1)}%</p>
                <p>广告 ACoS：{(snapshot.metrics.acos * 100).toFixed(1)}%</p>
                <p>同店主力同类 ACoS：{(snapshot.metrics.peerAcos * 100).toFixed(1)}%</p>
              </div>
            ) : current.asin === STRATEGY_PRODUCT.asin && snapshotPending ? (
              <div className="flex items-center gap-2 rounded-lg border border-line bg-subtle p-4 text-sm text-muted-foreground">
                <Spinner className="size-4" />
                正在载入商品基线。
              </div>
            ) : current.asin === STRATEGY_PRODUCT.asin && snapshotError ? (
              <div className="rounded-lg border border-line bg-subtle p-4 text-sm text-muted-foreground">
                <ErrorState
                  message="商品基线暂时无法载入，请重试。"
                  onRetry={() => void refetch()}
                  variant="inline"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-line bg-subtle p-4 text-sm text-muted-foreground">
                <IconInfoCircle className="size-4 shrink-0" />
                暂无投放数据。
              </div>
            )}
          </>
        )}
      </div>
      <SheetFooter className="border-t border-line px-7 py-5">
        <Button variant="outline" className="self-end" onClick={onClose}>
          关闭
        </Button>
      </SheetFooter>
    </>
  );
}
